import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { offlineStorage, OfflinePendingAction } from '../services/offlineStorage';

// How often to retry while visits are waiting. navigator.onLine stays true on
// a weak signal, so there may be no 'online' event when the signal recovers.
const RETRY_INTERVAL_MS = 60 * 1000;
// Stop waiting for the service worker's reply after this long; its own guard
// keeps a slow pass from being run twice.
const SYNC_RESPONSE_TIMEOUT_MS = 2 * 60 * 1000;
// IndexedDB metadata key where the service worker lists delivered drafts.
const SYNCED_WORKFLOWS_KEY = 'syncedWorkflowIds';

interface PendingAction {
  id: string;
  type: 'supervision' | 'patient' | 'employee' | 'user';
  action: 'create' | 'update' | 'delete';
  data: any;
  timestamp: number;
  retryCount: number;
  lastError?: string | null;
}

interface NetworkStatusContextType {
  isOnline: boolean;
  isConnecting: boolean;
  pendingActions: PendingAction[];
  lastSyncTime: Date | null;
  
  // Actions
  addPendingAction: (action: Omit<PendingAction, 'id' | 'timestamp' | 'retryCount'>) => Promise<void>;
  removePendingAction: (id: string) => Promise<void>;
  removePendingActionsForWorkflow: (workflowId: string) => Promise<void>;
  syncPendingActions: () => Promise<void>;
  clearPendingActions: () => void;
  retryFailedAction: (id: string) => Promise<void>;
  
  // Status helpers
  isActionPending: (type: string, actionType: string, itemId?: string) => boolean;
  getPendingCount: () => number;
  getFailedActions: () => PendingAction[];
}

const NetworkStatusContext = createContext<NetworkStatusContextType | undefined>(undefined);

const toPendingAction = (action: OfflinePendingAction): PendingAction => ({
  id: action.id!,
  type: action.type,
  action: action.action,
  data: action.data,
  timestamp: action.timestamp,
  retryCount: action.retryCount || 0,
  lastError: action.lastError
});

// Delete queued supervision sends for a draft from IndexedDB, except keepId.
const deleteQueuedCopies = async (workflowId: string, keepId?: string) => {
  const queued = await offlineStorage.getPendingActions();
  for (const queuedAction of queued) {
    if (
      queuedAction.type === 'supervision' &&
      queuedAction.data?.workflowId === workflowId &&
      queuedAction.id &&
      queuedAction.id !== keepId
    ) {
      await offlineStorage.deletePendingAction(queuedAction.id);
    }
  }
};

// Utility function to register background sync
const registerBackgroundSync = async (tag: string = 'offline-data-sync') => {
  const registrationPrototype = (window as any).ServiceWorkerRegistration?.prototype;
  if ('serviceWorker' in navigator && registrationPrototype && 'sync' in registrationPrototype) {
    try {
      const registration = await navigator.serviceWorker.ready;
      if ('sync' in registration) {
        await (registration as any).sync.register(tag);
        console.log(`[NetworkStatus] Background sync registered with tag: ${tag}`);
      } else {
        console.log('[NetworkStatus] Background sync not supported on this registration');
      }
    } catch (error) {
      console.error('[NetworkStatus] Background sync registration failed:', error);
    }
  } else {
    console.log('[NetworkStatus] Background sync not supported');
  }
};

interface NetworkStatusProviderProps {
  children: ReactNode;
}

export const NetworkStatusProvider: React.FC<NetworkStatusProviderProps> = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isConnecting, setIsConnecting] = useState(false);
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const syncInFlightRef = useRef(false);

  const refreshPendingActions = useCallback(async () => {
    const updatedActions = await offlineStorage.getPendingActions();
    setPendingActions(updatedActions.map(toPendingAction));
  }, []);

  // Remove Workflows drafts whose visits the service worker delivered,
  // including ones delivered while the app was closed.
  const clearDeliveredDrafts = useCallback(async () => {
    try {
      const delivered: string[] = (await offlineStorage.getMetadata(SYNCED_WORKFLOWS_KEY)) || [];
      if (delivered.length === 0) {
        return;
      }
      const { workflowService } = await import('../services/workflowService');
      delivered.forEach(workflowId => workflowService.deleteWorkflow(workflowId));
      // Keep any ids the service worker added while these were being cleared.
      const latest: string[] = (await offlineStorage.getMetadata(SYNCED_WORKFLOWS_KEY)) || [];
      await offlineStorage.setMetadata(SYNCED_WORKFLOWS_KEY, latest.filter(id => !delivered.includes(id)));
    } catch (error) {
      console.error('[NetworkStatus] Failed to clear delivered drafts:', error);
    }
  }, []);

  // Ask the service worker to send everything waiting in IndexedDB. Reads the
  // queue fresh each time so it never acts on a stale snapshot of state.
  const syncPendingActions = useCallback(async () => {
    if (!navigator.onLine || syncInFlightRef.current || !('serviceWorker' in navigator)) {
      return;
    }

    let waiting: OfflinePendingAction[];
    try {
      waiting = await offlineStorage.getPendingActions();
    } catch (error) {
      console.error('[NetworkStatus] Could not read waiting actions:', error);
      return;
    }
    if (waiting.length === 0) {
      return;
    }

    syncInFlightRef.current = true;
    setIsConnecting(true);

    try {
      const registration = await navigator.serviceWorker.ready;
      const worker = registration.active;
      if (!worker) {
        console.log('[NetworkStatus] Service worker not active, skipping sync');
        return;
      }

      const result = await new Promise<{ success: boolean; error?: string }>((resolve) => {
        const messageChannel = new MessageChannel();
        const timer = setTimeout(
          () => resolve({ success: false, error: 'Service worker did not respond' }),
          SYNC_RESPONSE_TIMEOUT_MS
        );
        messageChannel.port1.onmessage = (event) => {
          clearTimeout(timer);
          resolve(event.data || { success: false });
        };
        worker.postMessage({ type: 'TRIGGER_SYNC' }, [messageChannel.port2]);
      });

      if (result.success) {
        console.log('[NetworkStatus] Service worker sync completed successfully');
        setLastSyncTime(new Date());
      } else {
        console.error('[NetworkStatus] Service worker sync failed:', result.error);
      }
    } catch (error) {
      console.error('[NetworkStatus] Sync trigger failed:', error);
    } finally {
      syncInFlightRef.current = false;
      setIsConnecting(false);
      try {
        await refreshPendingActions();
      } catch (error) {
        console.error('[NetworkStatus] Failed to reload pending actions after sync:', error);
      }
      await clearDeliveredDrafts();
    }
  }, [refreshPendingActions, clearDeliveredDrafts]);

  // Listeners registered once below call the latest version through this ref.
  const syncRef = useRef(syncPendingActions);
  syncRef.current = syncPendingActions;

  // Load pending actions from IndexedDB on initialization
  useEffect(() => {
    const loadPendingActions = async () => {
      try {
        // Try to load from IndexedDB first (preferred)
        const offlineActions = await offlineStorage.getPendingActions();
        if (offlineActions.length > 0) {
          setPendingActions(offlineActions.map(toPendingAction));
        } else {
          // Fallback to localStorage for migration
          const stored = localStorage.getItem('lighthouse-pending-actions');
          if (stored) {
            const actions = JSON.parse(stored);
            setPendingActions(actions);

            // Migrate to IndexedDB
            for (const action of actions) {
              await offlineStorage.savePendingAction(action);
            }
            // Clear localStorage after migration
            localStorage.removeItem('lighthouse-pending-actions');
          }
        }
      } catch (error) {
        // If database is not initialized yet, silently ignore and try again later
        if (error instanceof Error && error.message.includes('Database not initialized')) {
          console.log('Database not ready yet, will retry loading pending actions...');
          // Retry after a short delay
          setTimeout(loadPendingActions, 100);
          return;
        }
        console.error('Failed to load pending actions:', error);
        return;
      }

      // Send anything left waiting from an earlier session, and clear drafts
      // delivered while the app was closed.
      syncRef.current();
      clearDeliveredDrafts();
    };

    loadPendingActions();
  }, [clearDeliveredDrafts]);

  // No longer saving to localStorage - using IndexedDB directly

  // Network status listeners and retry triggers
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncRef.current();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setIsConnecting(false);
    };

    // Phones suspend the app in the background; retry when it is reopened.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncRef.current();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    const retryTimer = setInterval(() => syncRef.current(), RETRY_INTERVAL_MS);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(retryTimer);
    };
  }, []);

  // Listen for service worker messages (automatic sync completion)
  useEffect(() => {
    const handleServiceWorkerMessage = async (event: MessageEvent) => {
      // Essential logging for functionality (DO NOT REMOVE - affects timing/closures)
      console.log('[NetworkStatus] 🔧 DEBUG: Received SW message:', event.data?.type, event.data);
      
      if (event.data?.type === 'SYNC_COMPLETED') {
        console.log('[NetworkStatus] 🔧 DEBUG: Processing SYNC_COMPLETED');
        console.log('[NetworkStatus] 🔧 DEBUG: Sync data:', event.data.data);
        
        try {
          // Reload pending actions from IndexedDB to get updated state
          await refreshPendingActions();

          // Update last sync time if sync was successful
          if (event.data.data?.successCount > 0) {
            setLastSyncTime(new Date());
            console.log('[NetworkStatus] 🔧 DEBUG: Updated last sync time');
          }
        } catch (error) {
          console.error('[NetworkStatus] ❌ Failed to refresh pending actions after service worker sync:', error);
        }
        await clearDeliveredDrafts();
      } else if (event.data?.type === 'CLEANUP_WORKFLOW') {
        console.log('[NetworkStatus] 🔧 DEBUG: Processing CLEANUP_WORKFLOW request:', event.data?.data?.workflowId);
        
        try {
          const workflowId = event.data?.data?.workflowId;
          if (!workflowId) {
            console.log('[NetworkStatus] ❌ No workflowId provided in cleanup message');
            return;
          }
          
          // Access workflow service to delete the workflow
          const { workflowService } = await import('../services/workflowService');
          await workflowService.deleteWorkflow(workflowId);
          console.log('[NetworkStatus] ✅ Successfully cleaned up workflow:', workflowId);
          
        } catch (error) {
          console.error('[NetworkStatus] ❌ Failed to cleanup workflow from service worker message:', error);
        }
      } else {
        console.log('[NetworkStatus] 🔧 DEBUG: Ignoring unknown message type:', event.data?.type);
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, [refreshPendingActions, clearDeliveredDrafts]);

  const generateActionId = () => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  // After a visit is sent directly, older queued copies are no longer needed.
  const removePendingActionsForWorkflow = useCallback(async (workflowId: string) => {
    try {
      await deleteQueuedCopies(workflowId);
    } catch (error) {
      console.error('[NetworkStatus] Failed to remove queued copies of sent visit:', error);
    }
    setPendingActions(prev => prev.filter(action => action.data?.workflowId !== workflowId));
  }, []);

  const addPendingAction = useCallback(async (action: Omit<PendingAction, 'id' | 'timestamp' | 'retryCount'>) => {
    const newAction: PendingAction = {
      ...action,
      id: generateActionId(),
      timestamp: Date.now(),
      retryCount: 0,
    };

    const workflowId: string | undefined = action.type === 'supervision' ? action.data?.workflowId : undefined;

    try {
      // Save to IndexedDB for service worker access
      await offlineStorage.savePendingAction({
        id: newAction.id,
        type: newAction.type,
        action: newAction.action,
        data: newAction.data,
        timestamp: newAction.timestamp,
        retryCount: newAction.retryCount
      });
      
      // Keep one waiting copy per visit: the newest save replaces older ones.
      // Deleted only after the new copy is safely stored; if this fails the
      // server still keeps just the newest version, so don't fail the save.
      if (workflowId) {
        try {
          await deleteQueuedCopies(workflowId, newAction.id);
        } catch (cleanupError) {
          console.error('[NetworkStatus] Failed to remove older queued copies:', cleanupError);
        }
      }

      // Update state for UI
      setPendingActions(prev => [
        ...prev.filter(existing => !workflowId || existing.data?.workflowId !== workflowId),
        newAction
      ]);
    } catch (error) {
      // Not saved anywhere that survives a reload, so tell the caller rather
      // than report a queued save that will never be sent.
      console.error('[NetworkStatus] Failed to save pending action:', error);
      throw error;
    }

    // Register background sync even when "online": the device may report a
    // connection it cannot actually use. Unsupported browsers rely on the
    // retry triggers in this provider instead.
    await registerBackgroundSync('offline-data-sync');
    console.log(`[NetworkStatus] Queued action: ${newAction.type} ${newAction.action}`);
  }, []);

  const removePendingAction = useCallback(async (id: string) => {
    try {
      // Remove from IndexedDB
      await offlineStorage.deletePendingAction(id);
      // Update state
      setPendingActions(prev => prev.filter(action => action.id !== id));
    } catch (error) {
      console.error('[NetworkStatus] Failed to remove pending action:', error);
      // Still update UI even if IndexedDB fails
      setPendingActions(prev => prev.filter(action => action.id !== id));
    }
  }, []);

  const clearPendingActions = useCallback(() => {
    setPendingActions([]);
  }, []);

  const retryFailedAction = useCallback(async (id: string) => {
    const action = pendingActions.find(a => a.id === id);
    if (!action || !isOnline) {
      return;
    }

    try {
      // Simulate retry logic
      const success = Math.random() > 0.3; // 70% success rate for retries
      
      if (success) {
        removePendingAction(id);
        console.log(`Successfully retried action ${id}`);
      } else {
        // Increment retry count
        setPendingActions(prev =>
          prev.map(a => 
            a.id === id 
              ? { ...a, retryCount: a.retryCount + 1 }
              : a
          )
        );
        throw new Error('Retry failed');
      }
    } catch (error) {
      console.error(`Retry failed for action ${id}:`, error);
      throw error;
    }
  }, [isOnline, pendingActions, removePendingAction]);

  const isActionPending = useCallback((type: string, actionType: string, itemId?: string) => {
    return pendingActions.some(action => 
      action.type === type && 
      action.action === actionType &&
      (!itemId || action.data?.id === itemId)
    );
  }, [pendingActions]);

  const getPendingCount = useCallback(() => pendingActions.length, [pendingActions]);

  const getFailedActions = useCallback(() => {
    return pendingActions.filter(action => action.retryCount > 0);
  }, [pendingActions]);

  const value: NetworkStatusContextType = {
    isOnline,
    isConnecting,
    pendingActions,
    lastSyncTime,
    addPendingAction,
    removePendingAction,
    removePendingActionsForWorkflow,
    syncPendingActions,
    clearPendingActions,
    retryFailedAction,
    isActionPending,
    getPendingCount,
    getFailedActions,
  };

  return (
    <NetworkStatusContext.Provider value={value}>
      {children}
    </NetworkStatusContext.Provider>
  );
};

export const useNetworkStatus = (): NetworkStatusContextType => {
  const context = useContext(NetworkStatusContext);
  if (context === undefined) {
    throw new Error('useNetworkStatus must be used within a NetworkStatusProvider');
  }
  return context;
};

export default useNetworkStatus;